import { Cell, XFlowGraph, useGraphEvent, useGraphInstance } from "@antv/xflow"
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react"
import { useEditorEvents } from "./events/useEditorEvents"
import { onCreateEdge, onCreateLine } from "./edges"
import { CellView, Dom, Edge, Node } from "@antv/x6"
import { FlowsheetContent, LineEndEnum, NodeShapes, SheetContent } from "./services/modal"
import { resetSheet, resetTempCells } from "./services/resetCells"
import { Export } from "@antv/x6-plugin-export"
import { eventToObserverable, isALinePort, isNotStream } from "./events/helper"
import { debounceTime, filter, merge } from "rxjs"
import { getLineEndType } from "./utils"
import { DrawingMode, LineType, PortTypes } from "./constant"
import { get1MM } from "@/utils/size"
import { useHasPermissions } from "../OPermission/OPermission"
import { ROLE_DRAFTER } from "../OPermission/PlanPermission"

interface EditorGraphProps {
  onSelected?: (params: any) => void
  content: SheetContent
  onChange?: () => void
  template?: FlowsheetContent
  drawingStream?: DrawingMode
  onRemove?: (cell: Cell) => void
}

export const GridSize = get1MM() * 2.5;

export const EditorGraph = forwardRef((props: EditorGraphProps, ref) => {
  const {onSelected, content, onChange, drawingStream, template, onRemove} = props
  const isDrawingStream = drawingStream === DrawingMode.Stream
  useEditorEvents(isDrawingStream, false)
  const graph = useGraphInstance()
  const initRef = useRef(false)
  const hasPermission = useHasPermissions();
  useGraphEvent<any>('cell:click', (params) => {
    onSelected?.(params)
  })

  const readOnly = useMemo(() => {
    return !hasPermission(ROLE_DRAFTER)
  }, [hasPermission])

  useEffect(() => {
    if (!graph) {
      return;
    }
    if (!onChange) return;

    const removeSub = eventToObserverable(graph, 'cell:removed').subscribe((evt: any) => {
      onRemove?.(evt.cell)
    })
    const names = ['cell:added', 'cell:removed'];
    const observers = names.map(item => {
      return eventToObserverable(graph, item).pipe(filter((evt: any) => {
        const cell = evt.cell as Cell;
        if (graph.isEdge(cell)) {
          if (cell.data.type === LineType.FlowLine) {
            return true;
          }
        } else {
          const node = cell as Node;
          if (node.shape === NodeShapes.SvgNode) {
            return true
          }
        }
        return false;
      }))
    })

    const edgeEvents = ['edge:change:source', 'edge:change:target']
    const edgeObservers = edgeEvents.map(item => {
      return eventToObserverable(graph, item).pipe(filter((evt: any) => {
        const {previous, current} = evt
        const prevType = getLineEndType(previous)
        const currType = getLineEndType(current)
        if (prevType === currType) {
          if (!(prevType === LineEndEnum.LinkToEdge && previous.cell !== current.cell)) {
            return false
          }
          const connectCell = graph.getCellById(current.cell) as Node
          const port = connectCell.getPort(current.port)
          if (port?.group === PortTypes.NozzlePort) {
            return true
          }
          return false;
        }
        if (prevType === LineEndEnum.LinkToPort || currType === LineEndEnum.LinkToPort) {
          const cell = current.cell || previous.cell
          const portId = current.port || previous.port
          const connectCell = graph.getCellById(cell) as Node
          const port = connectCell.getPort(portId)
          if (port?.group === PortTypes.NozzlePort) {
            return true
          }
          return false;
        }
        return false
      }))
    })
    
    const sub = merge(...observers, ...edgeObservers)
      .pipe(debounceTime(3000)) // root cause here.
      .subscribe(() => {
        onChange()
      })

    return () => {
      sub.unsubscribe()
      removeSub.unsubscribe()
    }
  }, [graph, onChange, onSelected])

  useImperativeHandle(
    ref,
    () => ({
      getGraph: () => graph
    }),
    [graph]
  )

  useEffect(() => {
    if (graph && !initRef.current) {
      resetSheet(content, graph!, readOnly)
      initRef.current = true;
    }
  }, [content, graph, readOnly])

  useEffect(() => {
    if (!graph) return;
    if (!template) {
      return;
    }
    const overrideNodes = content.overridedNodes || []
    const tempData = (template.tempCells || []).map(item => {
      const target = overrideNodes.find((text) => text.id === item.id)
      if (target) {
        return target
      }
      return item
    })
    
    resetTempCells(tempData, graph, false)
  }, [template, graph, content])

  useEffect(() => {
    // for test
    if (!graph) return;
    (window as any).__x6_instances__ = [];
    (window as any).__x6_instances__.push(graph)
  }, [graph])

  useEffect(() => {
    if (!graph) return;
    graph.options.connecting.validateMagnet = (params: {cell: Cell, magnet: Element}) => {
      if (isDrawingStream) {
        return false
      }
      return true
    }
  }, [isDrawingStream, graph])

  useEffect(() => {
    if (!graph) return;
    graph.use(new Export())
    graph.options.connecting.createEdge = function customCreateEdge(params: {
      sourceCell: Cell
      sourceView: CellView
      sourceMagnet: Element,
    }) {
      let options;
      if (isALinePort(params.sourceMagnet)) {
        options = onCreateLine(params)
      } else {
        options = onCreateEdge(params)
      }
      return this.createEdge(options);
    }
    if (hasPermission(ROLE_DRAFTER)) {
      const interacting = graph.options.interacting || {} as any;
      interacting.nodeMovable = (view) => {
        const cell = view.cell
        return ![NodeShapes.TextNode, NodeShapes.ImageNode].includes(cell.shape);
      }
    } else {
      graph.options.interacting = false;
    }
  }, [graph, hasPermission])
  
  return <>
    <XFlowGraph
      scroller
      scrollerOptions={{
        padding: {
          left: 0,
          top: 0,
          bottom: 0,
          right: 0
        }
      }}
      zoomable
      pannable
      virtual={false}
      minScale={0.5}
      maxScale={2}
      connectionOptions={{
        router: 'normal',
        connectionPoint: 'anchor',
        allowBlank: true,
        allowMulti: true,
        allowLoop: false,
        allowNode: false,
        allowEdge: ({edge, targetCell, sourceCell}) => {
          const anotherEdge = sourceCell || targetCell;
          if (!graph?.isEdge(anotherEdge as Cell)) return true;
          if (isNotStream(edge as Edge) || isNotStream(anotherEdge as Edge)) return false;
          return true;
        },
        allowPort: true,
      }}
      zoomOptions={{
        global: true,
        modifiers: ['ctrl', 'meta'],
      }} />
  </>
});